import { ApiError } from './ApiError.ts';
import Config from './Config.ts';
import {
  ConfirmationResponse,
  PostApiV20Verification,
  PostApiV20Verify,
  ReservationResponse,
  RestaurantResponse,
  SetPaymentMethodResponse,
  SetupIntentResponse,
  AuthResponse,
  Post_Reserve,
  Post_Confirm,
  GetReservationResponse,
  Post_PaymentMethod,
  Post_UpdateUser,
  UpdateUserResponse,
  ReserveErrorResponse,
  UpdateUserErrorResponse,
  CardErrorResponse,
  GetUserResponse,
  GetUserCreditsResponse,
  Post_Claim,
  PaymentMethodsResponse,
  CancelConfirmResponse,
  CancelFinishResponse,
  Post_Cancel,
  ReservationReceiptResponse,
  Post_Tip,
  Post_AdditionalTip,
  ReservationPreviewResponse,
  ActionRequiredResponse,
  EventReservationPayload,
  EventReservationResponse,
  PaymentType,
  ConfirmationResponseReservation,
} from './types.ts';
import type {
  RequestOptionsResponse,
  CustomRequestSettingsResponse,
  CustomRequestPreviewBody,
  CustomRequestSubmitBody,
} from '../apps/Widget/lib/types.ts';
import * as Sentry from '@sentry/react';

export type SearchParams = undefined | Record<string, string | number | boolean | null | undefined>;

const API_URI = {
  RESTAURANT: {
    GET: (id: number) => `public/restaurant/${id}`,
    CONFIRM: (id: number) => `public/restaurant/${id}/confirm`,
    RESERVE: (id: number) => `public/restaurant/${id}/reserve`,
    REQUEST_OPTIONS: (id: number) => `public/restaurant/${id}/request-options`,
    CUSTOM_REQUEST_PREVIEW: (id: number) => `public/restaurant/${id}/custom-request/preview`,
    CUSTOM_REQUEST: (id: number) => `public/restaurant/${id}/custom-request`,
  },
  CONCIERGE_MEMBER: {
    PREVIEW: (uuid: string) => `v20240524/concierge-reservations/${uuid}/preview`,
    RECEIPT: (uuid: string) => `v20240524/concierge-reservations/${uuid}/receipt`,
    TIP: (uuid: string) => `v20240524/concierge-reservations/${uuid}/tip`,
    ADDITIONAL_TIP: (uuid: string) => `v20240524/concierge-reservations/${uuid}/additional_tip`,
  },
  EVENT: {
    PREVIEW: (id: number, eventId: number) => `restaurant/${id}/event/${eventId}/reserve/preview`,
    RESERVE: (id: number, eventId: number) => `restaurant/${id}/event/${eventId}/reserve`,
  },
  RESERVATION: {
    METADATA: (uuid: string) => `public/reservation/${uuid}/metadata`,
    PREVIEW: (uuid: string) => `v2.1/reservations/${uuid}/preview`,
    RECEIPT: (uuid: string) => `v2.1/reservations/${uuid}/receipt`,
    TIP: (uuid: string) => `v2.0/reservations/${uuid}/tip`,
    ADDITIONAL_TIP: (uuid: string) => `v2.0/reservations/${uuid}/additional_tip`,
    CLAIM: (id: number, uuid: string) => `public/reservation/${id}/${uuid}/claim`,
    CLAIM_FOR_UNCLAIMED_RESERVATION: (id: number, uuid: string) =>
      `public/reservation/${id}/${uuid}/claim/for_unclaimed_reservation`,
    CANCEL: {
      CONFIRM: (uuid: string, signature: string) =>
        `public/reservation/${uuid}/cancel-confirm?sig=${signature}`,
      FINISH: (uuid: string, signature: string) =>
        `public/reservation/${uuid}/cancel?sig=${signature}`,
      AUTH_CONFIRM: (uuid: string) => `reservation/${uuid}/cancel-confirm`,
      AUTH_FINISH: (uuid: string) => `reservation/${uuid}/cancel`,
    },
  },
  USER: {
    GET: () => 'user',
    RESERVATION: (uuid: string) => `user/reservation/${uuid}`,
    SETUP_INTENT: () => 'setup-intent',
    SETUP_INTENT_FOR_UNCLAIMED_RESERVATION: (uuid) =>
      `setup-intent-for-unclaimed-reservation/${uuid}`,
    PAYMENT_METHOD: () => 'v2.0/billings',
    PAYMENT_METHOD_FOR_UNCLAIMED_RESERVATION: (uuid: string) =>
      `v2.0/billings_for_unclaimed_reservation/${uuid}?XDEBUG_SESSION_START=1`,
    ADD_PAYMENT_METHOD: () => 'add-payment-method',
    ADD_PAYMENT_METHOD_FOR_UNCLAIMED_RESERVATION: (uuid: string) =>
      `add-payment-method-for-unclaimed-reservation/${uuid}`,
    UPDATE: () => 'user',
    UPDATE_FOR_UNCLAIMED_RESERVATION: (uuid: string) => `user/for_unclaimed_reservation/${uuid}`,
    CREDITS: () => 'user/credits',
    SUBSCRIBE_TO_GENERAL_MARKETING_SMS: () => 'v2.0/user/subscribe-to-general-marketing-sms',
    ACCEPT_TERMS: () => 'v2.0/user/accept-terms',
  },
  PHONE: {
    VERIFY: () => 'v2.0/verify',
    VERIFICATION: () => 'v2.0/verification',
  },
  PLAID: {
    CREATE_LINK_TOKEN: () => 'plaid/create_link_token',
    CREATE_PAYMENT_METHOD: () => 'plaid/payment-method/create',
  },
};

const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-API-Version': '20240524',
};

const GET_OPTIONS: RequestInit = {
  method: 'GET',
  mode: 'cors',
  //referrerPolicy: 'strict-origin-when-cross-origin',
};

const POST_OPTIONS: RequestInit = {
  method: 'POST',
  mode: 'cors',
  // referrerPolicy: 'strict-origin-when-cross-origin',
};

const api = {
  get: async <ResponseT, SearchT extends SearchParams = undefined>(
    token: string | null,
    path: string,
    search_params?: SearchT
  ): Promise<ResponseT> => {
    const url = new URL(`${Config.data.uri.api}${path}`);
    if (search_params) {
      Object.keys(search_params)
        .filter((key) => !!search_params[key])
        .forEach((key) => url.searchParams.append(key, '' + search_params[key]));
    }

    const options: RequestInit = {
      ...GET_OPTIONS,
      headers: {
        ...headers,
        'X-Dorsia-Client': `widget build:${Config.data.build},environment:${Config.data.environment}`,
      },
    };

    if (token) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    let json: ResponseT = {} as ResponseT;
    let response: Response | null = null;
    try {
      response = await fetch(url, options);
    } catch (e: unknown) {
      console.warn(
        'Uncaught Exception',
        url.toString(),
        options,
        e instanceof Error ? e.message : ''
      );
    }

    if (response) {
      try {
        json = (await response.json()) as ResponseT;
      } catch {
        console.warn('Json Parsing issue', url.toString());
      }

      if (!response.ok) {
        const type = ApiError.match<ResponseT>(json);
        if (type) {
          if (type === 'UnfinishedUserProfileErrorResponse') {
            return json;
          }

          const resp = json as unknown as ReserveErrorResponse | UpdateUserErrorResponse;
          throw new ApiError({ url, response: resp });
        }

        // ApiError.match() only recognizes bodies with a `type` field or one
        // of a handful of hardcoded `message` strings. Some endpoints (e.g.,
        // the reservation endpoints returning `error_code: 'IDV_REQUIRED'`)
        // return an error body that matches neither. Throw ApiError anyway so
        // callers can inspect `response.error_code` — without this, the
        // fetch would silently return the error body as if it were a success.
        const errorCode = (json as unknown as Record<string, unknown>)?.error_code;
        if (typeof errorCode === 'string' && errorCode.length > 0) {
          throw new ApiError({
            url,
            response: json as unknown as ReserveErrorResponse,
          });
        }

        Sentry.captureMessage('api.get Error', {
          extra: {
            response: {
              status: response.status,
              statusText: response.statusText,
              body: json,
            },
          },
        });

        // throw new Error(response.statusText);
      }
    }

    return json;
  },

  post: async <ResponseT, PostT = undefined, SearchT extends SearchParams = undefined>(
    token: string | null,
    path: string,
    search_params?: SearchT,
    post_params?: PostT
  ): Promise<ResponseT> => {
    const url = new URL(`${Config.data.uri.api}${path}`);
    if (search_params) {
      Object.keys(search_params).forEach((key) => url.searchParams.append(key, '' + key));
    }

    const options: RequestInit = {
      ...POST_OPTIONS,
      headers: {
        ...headers,
        'X-Dorsia-Client': `widget build:${Config.data.build},environment:${Config.data.environment}`,
      },
      body: JSON.stringify(post_params),
    };

    if (token) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    let json: ResponseT = {} as ResponseT;
    let response: Response | null = null;
    try {
      response = await fetch(url, options);
    } catch (e: unknown) {
      console.warn(
        'Uncaught Exception',
        url.toString(),
        options,
        e instanceof Error ? e.message : ''
      );
    }

    if (response) {
      try {
        json = (await response.json()) as ResponseT;
      } catch {
        console.warn('Json Parsing issue', url.toString());
      }

      if (!response.ok) {
        const type = ApiError.match<ResponseT>(json);
        if (type) {
          const resp = json as unknown as
            | ReserveErrorResponse
            | UpdateUserErrorResponse
            | CardErrorResponse
            | ActionRequiredResponse;

          throw new ApiError({ url, response: resp });
        }

        // ApiError.match() only recognizes bodies with a `type` field or a
        // hardcoded `message`. Reservation endpoints (e.g., POST /reserve)
        // return `{ message:'Error', description, error_code:'IDV_REQUIRED' }`
        // which matches neither — without this fallback the error body would
        // silently return as if it were a success and IDV copy would never
        // render. Mirrors the same fallback in api.get above.
        const errorCode = (json as unknown as Record<string, unknown>)?.error_code;
        if (typeof errorCode === 'string' && errorCode.length > 0) {
          throw new ApiError({
            url,
            response: json as unknown as ReserveErrorResponse,
          });
        }

        // throw new Error(response.statusText);
      }
    }

    return json;
  },
};

export const getUserReservation = async (
  token: string,
  uuid: string
): Promise<GetReservationResponse> => {
  return api.get(token, API_URI.USER.RESERVATION(uuid));
};

/**
 * Fetches minimal public metadata for a reservation (no auth required).
 * Used to show booking context (restaurant name, date, party size) before phone verification.
 * @param uuid - Reservation UUID
 * @returns Public metadata suitable for link previews and auth gates
 */
export interface ReservationMetadataResponse {
  title: string;
  description: string | null;
  organization_name: string | null;
  restaurant_name: string;
  date: string | null;
  time: string | null;
  party_size: number | null;
  image_url: string | null;
}

export const getReservationMetadata = async (
  uuid: string
): Promise<ReservationMetadataResponse> => {
  return api.get(null, API_URI.RESERVATION.METADATA(uuid));
};

export const createReservation = async (
  token: string,
  body: Post_Reserve
): Promise<ReservationResponse> => {
  return api.post(token, API_URI.RESTAURANT.RESERVE(body.restaurant), undefined, body);
};

export const createEventReservationPreview = async (
  token: string,
  body: EventReservationPayload,
  eventId: number,
  restaurantId: number
): Promise<EventReservationResponse> => {
  return api.post(token, API_URI.EVENT.PREVIEW(restaurantId, eventId), undefined, body);
};

export const createEventReservationBooking = async (
  token: string,
  body: EventReservationPayload,
  eventId: number,
  restaurantId: number
): Promise<EventReservationResponse> => {
  return api.post(token, API_URI.EVENT.RESERVE(restaurantId, eventId), undefined, body);
};

export const getEventReservation = async (
  token: string,
  body: EventReservationPayload,
  eventId: number,
  restaurantId: number
): Promise<EventReservationResponse> => {
  return api.post(token, API_URI.EVENT.RESERVE(restaurantId, eventId), undefined, body);
};

/**
 * The wire shape returned by `getEventsByRestaurantId`. `id` is `ID!` in the
 * schema — a string on the wire — and `name`/`date` are `String!` but marked
 * optional here defensively. `EventPage` normalizes this into `BookingEvent`
 * (numeric id, required name/date) at the fetch boundary; keeping the two
 * types distinct preserves that validation instead of erasing it.
 *
 * Multi-day: `datetime_local_end__formatted` is the authoritative signal —
 * the API returns it non-null only for genuine multi-day spans
 * (`Event::getDatetimeLocalEndFormattedAttribute`). `date_end` is intentionally
 * NOT fetched: it's auto-rolled to the next calendar day on save for
 * after-midnight one-nighters (9 PM -> 2 AM), so it cannot distinguish a
 * real span from an overnight rollover.
 */
export interface RawBookingEvent {
  id: string;
  name?: string;
  date?: string;
  time?: string;
  datetime_end_utc?: string | null;
  datetime_local__formatted?: {
    date_dow_short?: string;
    date_short?: string;
  } | null;
  datetime_local_end__formatted?: {
    date_dow_short?: string;
    date_short?: string;
  } | null;
  sold_out?: boolean;
}

/**
 * The validated event model the Booking EventPage renders from. Differs from
 * `RawBookingEvent` only at the fetch boundary: `id` is the parsed numeric id
 * (the `Number()` + `Number.isFinite` guard in the normalizer is the only
 * place that conversion happens), and `name`/`date` are required as the
 * schema promises (`String!`).
 */
export interface BookingEvent {
  id: number;
  name: string;
  date: string;
  time?: string;
  datetime_end_utc?: string | null;
  datetime_local__formatted?: {
    date_dow_short?: string;
    date_short?: string;
  } | null;
  datetime_local_end__formatted?: {
    date_dow_short?: string;
    date_short?: string;
  } | null;
  sold_out?: boolean;
}

interface GraphqlEventsByRestaurantResponse {
  data?: {
    getEventsByRestaurantId?: RawBookingEvent[];
  };
}

interface GraphqlAccessResponse {
  data?: {
    accessTypes?: Array<{
      id: number;
      name: string;
      description?: string;
      coming_soon?: boolean;
      price_summary?: string;
      guest_summary?: string;
    }>;
  };
}

export interface TicketInventoryRow {
  id: number;
  price: number;
  price__currency: string;
  sold_out: boolean;
  guests: number;
  maximum_purchasable: number;
  has_tiered_pricing?: boolean;
  tiers?: Array<{
    id: string;
    is_sold_out: boolean;
  }>;
  allowedPaymentTypes?: PaymentType[];
  item: {
    name?: string;
    description?: string;
    coming_soon?: boolean;
    requires_idv?: boolean;
  };
}

interface GraphqlTicketResponse {
  data?: {
    ticketInventory?: TicketInventoryRow[];
  };
}

// Bookable = not sold out and not coming soon, tier-aware: a tiered row is
// sold out only when every tier is (the parent sold_out flag can lag).
export const isTicketBookable = (row: TicketInventoryRow): boolean => {
  if (row.item?.coming_soon) return false;
  if (row.has_tiered_pricing && row.tiers && row.tiers.length > 0) {
    return !row.tiers.every((tier) => tier.is_sold_out);
  }
  return !row.sold_out;
};

export const getReservationPreview = async (
  token: string,
  uuid: string
): Promise<ReservationPreviewResponse> => {
  return api.get(token, API_URI.RESERVATION.PREVIEW(uuid));
};

export const getReservationReceipt = async (
  token: string,
  uuid: string
): Promise<ReservationReceiptResponse> => {
  return api.get(token, API_URI.RESERVATION.RECEIPT(uuid));
};

export const updateReservationTip = async (
  token: string,
  uuid: string,
  body: Post_Tip
): Promise<ReservationReceiptResponse> => {
  return api.post(token, API_URI.RESERVATION.TIP(uuid), undefined, body);
};

export const updateReservationAdditionalTip = async (
  token: string,
  uuid: string,
  body: Post_AdditionalTip
): Promise<ReservationReceiptResponse> => {
  return api.post(token, API_URI.RESERVATION.ADDITIONAL_TIP(uuid), undefined, body);
};

export const getConciergeMemberReservationPreview = async (
  token: string,
  uuid: string
): Promise<ReservationPreviewResponse> => {
  return api.get(token, API_URI.CONCIERGE_MEMBER.PREVIEW(uuid));
};

export const getConciergeMemberReservationReceipt = async (
  token: string,
  uuid: string
): Promise<ReservationReceiptResponse> => {
  return api.get(token, API_URI.CONCIERGE_MEMBER.RECEIPT(uuid));
};

export const updateConciergeMemberReservationTip = async (
  token: string,
  uuid: string,
  body: Post_Tip
): Promise<ReservationReceiptResponse> => {
  return api.post(token, API_URI.CONCIERGE_MEMBER.TIP(uuid), undefined, body);
};

export const updateConciergeMemberReservationAdditionalTip = async (
  token: string,
  uuid: string,
  body: Post_AdditionalTip
): Promise<ReservationReceiptResponse> => {
  return api.post(token, API_URI.CONCIERGE_MEMBER.ADDITIONAL_TIP(uuid), undefined, body);
};

export const claimReservation = async (
  token: string,
  id: number,
  uuid: string,
  body: Post_Claim | undefined
): Promise<ReservationResponse> => {
  return api.post(token, API_URI.RESERVATION.CLAIM(id, uuid), undefined, body);
};

export const claimReservationForUnclaimedReservation = async (
  id: number,
  uuid: string,
  body: Post_Claim | undefined
): Promise<ReservationResponse> => {
  return api.post(
    null,
    API_URI.RESERVATION.CLAIM_FOR_UNCLAIMED_RESERVATION(id, uuid),
    undefined,
    body
  );
};

export const cancelReservationConfirm = async (
  signature: string,
  uuid: string
): Promise<CancelConfirmResponse> => {
  return api.post(null, API_URI.RESERVATION.CANCEL.CONFIRM(uuid, signature));
};

export const cancelReservationFinish = async (
  signature: string,
  uuid: string,
  body: Post_Cancel
): Promise<CancelFinishResponse> => {
  return api.post(null, API_URI.RESERVATION.CANCEL.FINISH(uuid, signature), undefined, body);
};

/**
 * Fetches cancellation details for a reservation using authenticated token.
 * @param token - Auth token
 * @param uuid - Reservation UUID
 * @returns Cancellation confirmation details including type
 */
export const cancelReservationConfirmAuth = async (
  token: string,
  uuid: string
): Promise<CancelConfirmResponse> => {
  return api.post(token, API_URI.RESERVATION.CANCEL.AUTH_CONFIRM(uuid));
};

/**
 * Cancels a reservation using authenticated token.
 * @param token - Auth token
 * @param uuid - Reservation UUID
 * @param body - Cancellation body with type
 * @returns Cancellation result with charge/credit info
 */
export const cancelReservationAuth = async (
  token: string,
  uuid: string,
  body: Post_Cancel
): Promise<CancelFinishResponse> => {
  return api.post(token, API_URI.RESERVATION.CANCEL.AUTH_FINISH(uuid), undefined, body);
};

export const getReservationConfirmation = async (
  token: string,
  id: number,
  body: Post_Confirm
): Promise<ConfirmationResponse> => {
  return api.post(token, API_URI.RESTAURANT.CONFIRM(id), undefined, body);
};

export const getRestaurant = async (
  id: number | null,
  date: string,
  start_time: string,
  cache: string,
  token: string | null = null
): Promise<RestaurantResponse | null> => {
  if (!id) {
    return null;
  }

  if (!cache) {
    console.log('.');
  }

  const params = {
    date,
    start_time,
  };

  return await api.get(token, API_URI.RESTAURANT.GET(id), params);
};

export const setupIntent = async (token: string): Promise<SetupIntentResponse> => {
  return await api.get(token, API_URI.USER.SETUP_INTENT());
};

export const setupIntentForUnclaimedReservation = async (
  uuid: string
): Promise<SetupIntentResponse> => {
  return await api.get(null, API_URI.USER.SETUP_INTENT_FOR_UNCLAIMED_RESERVATION(uuid));
};

export const getPaymentMethods = async (token: string): Promise<PaymentMethodsResponse> => {
  return await api.get(token, API_URI.USER.PAYMENT_METHOD());
};

export const getPaymentMethodsForUnclaimedReservation = async (
  uuid: string
): Promise<PaymentMethodsResponse> => {
  return await api.get(null, API_URI.USER.PAYMENT_METHOD_FOR_UNCLAIMED_RESERVATION(uuid));
};

export const setPaymentMethod = async (
  token: string,
  body: Post_PaymentMethod
): Promise<SetPaymentMethodResponse> => {
  return await api.post(token, API_URI.USER.ADD_PAYMENT_METHOD(), undefined, body);
};

export const setPaymentMethodForUnclaimedReservation = async (
  uuid: string,
  body: Post_PaymentMethod
): Promise<SetPaymentMethodResponse> => {
  return await api.post(
    null,
    API_URI.USER.ADD_PAYMENT_METHOD_FOR_UNCLAIMED_RESERVATION(uuid),
    undefined,
    body
  );
};

export const getUser = async (token: string): Promise<GetUserResponse> => {
  return await api.get(token, API_URI.USER.GET());
};

export const updateUser = async (
  token: string,
  body: Post_UpdateUser
): Promise<UpdateUserResponse> => {
  return await api.post(token, API_URI.USER.UPDATE(), undefined, body);
};

export const updateUserForUnclaimedReservationWithoutAuth = async (
  uuid: string,
  body: Post_UpdateUser
): Promise<UpdateUserResponse> => {
  return await api.post(null, API_URI.USER.UPDATE_FOR_UNCLAIMED_RESERVATION(uuid), undefined, body);
};

export const getUserCredits = async (token: string): Promise<GetUserCreditsResponse> => {
  return await api.get(token, API_URI.USER.CREDITS());
};

export const verify = async (
  phone: string,
  channel?: string,
  recaptchaToken?: string
): Promise<null> => {
  const body: PostApiV20Verify = {
    phone: phone,
    channel: channel ? channel : null,
  };

  if (recaptchaToken) {
    body['g-recaptcha-response'] = recaptchaToken;
  }

  return api.post<null, PostApiV20Verify>(null, API_URI.PHONE.VERIFY(), undefined, body);
};

export const verification = async (
  phone: string,
  code: string,
  recaptchaToken?: string,
  subscribeToGeneralMarketingSms?: boolean
): Promise<AuthResponse> => {
  const body: PostApiV20Verification = { phone, code };

  if (recaptchaToken) {
    body['g-recaptcha-response'] = recaptchaToken;
  }

  // Opt-in only. Sending `false` would still be a no-op server-side (the
  // controller reads it with `boolean(..., false)` and only calls
  // `subscribeToGeneralMarketingSms` when truthy), but omitting the key keeps
  // the "never unsubscribe" invariant explicit at the wire boundary and
  // matches the Member app's PhoneValidation call.
  if (subscribeToGeneralMarketingSms) {
    body.subscribe_to_general_marketing_sms = true;
  }

  return api.post<AuthResponse, PostApiV20Verification>(
    null,
    API_URI.PHONE.VERIFICATION(),
    undefined,
    body
  );
};

/**
 * Opt the authenticated widget guest into the General Marketing SMS group in
 * Braze. The verify() call site above only fires for guests who need an OTP;
 * returning "older" widget users who already hold a token skip verify() and
 * would otherwise never reach it. This function is that opt-in path, and pairs
 * with `POST /v2.0/user/subscribe-to-general-marketing-sms` [GRO-7075].
 *
 * Opt-in only: callers should invoke this only when
 * `resolveWidgetMarketingSmsOptIn(widget)` is true. The endpoint never
 * unsubscribes; a "false" state is expressed by not calling it. A defense-in-
 * depth US-only country check also runs server-side in
 * `UserController::subscribeToGeneralMarketingSms` (returns 204 no-op for
 * non-US users) since `GeneralMarketingSms::isForUser()` only excludes GB, not
 * all non-US countries.
 *
 * @param token - Sanctum token from `AuthAtom`
 * @returns HTTP 204 on dispatch (the Braze update is async)
 * @example subscribeToGeneralMarketingSms(auth.token)
 */
export const subscribeToGeneralMarketingSms = async (token: string): Promise<null> => {
  return api.post<null, Record<string, never>>(
    token,
    API_URI.USER.SUBSCRIBE_TO_GENERAL_MARKETING_SMS(),
    undefined,
    {}
  );
};

/**
 * Record the authenticated guest's acceptance of Terms of Use + Privacy Policy.
 *
 * Backs DetailsPage's "By clicking Continue, you agree to our Terms of Use and
 * Privacy Policy" line so the Member app's terms gate does not later block a
 * widget booker on first launch. The backend endpoint is intentionally
 * idempotent (strictly append-once) and uses a conditional atomic
 * `UPDATE ... WHERE accepted_terms_at IS NULL` so concurrent requests cannot
 * overwrite the original acceptance timestamp — safe to fire on every widget
 * booking [GRO-7075].
 *
 * @param token - Sanctum token from `AuthAtom` (or a freshly-issued token from
 *                `verification()`)
 * @returns HTTP 204 whether or not a stamp was written
 * @example acceptTerms(auth.token)
 */
export const acceptTerms = async (token: string): Promise<null> => {
  return api.post<null, Record<string, never>>(token, API_URI.USER.ACCEPT_TERMS(), undefined, {});
};

export const graphqlEventsByRestaurantsQuery = async (
  restaurantId: number,
  token: string | null = null
): Promise<GraphqlEventsByRestaurantResponse> => {
  const sendQuery = `query GetEventsByRestaurantId($restaurantId: ID!) {
    getEventsByRestaurantId(id: $restaurantId) {
      id
      name
      min_membership_level
      date
      time
      datetime_end_utc
      datetime_local__formatted {
        date_dow_short
        date_short
      }
      datetime_local_end__formatted {
        date_dow_short
        date_short
      }
      sold_out
    }
  }`;

  return await api.post<
    GraphqlEventsByRestaurantResponse,
    {
      query: string;
      variables: { restaurantId: number };
    }
  >(token, '../graphql/public', undefined, {
    query: sendQuery,
    variables: {
      restaurantId: restaurantId,
    },
  });
};

export const graphqlAccessQuery = async (
  eventId: number,
  token: string | null = null
): Promise<GraphqlAccessResponse> => {
  const sendQuery = `query Access($forEvent: ID!) {
      accessTypes(forEvent: $forEvent) {
        id
        name
        description
        coming_soon
        price_summary
        guest_summary
        min_membership_level
    }
  }`;

  return await api.post<
    GraphqlAccessResponse,
    {
      query: string;
      variables: { forEvent: number };
    }
  >(token, '../graphql/public', undefined, {
    query: sendQuery,
    variables: {
      forEvent: eventId,
    },
  });
};

export const graphqlTicketQuery = async (
  eventId: number,
  accessTypeId: number,
  token: string | null = null
): Promise<GraphqlTicketResponse> => {
  const sendQuery = `query TicketInventory($forEvent: ID!, $accessType: ID!) {
        ticketInventory(forEvent: $forEvent, forAccessType: $accessType) {
            id
            price
            price__currency
            sold_out
            guests
            maximum_purchasable
            has_tiered_pricing
            tiers {
                id
                is_sold_out
            }
            item {
                name
                description
                coming_soon
                requires_idv
            }
            allowedPaymentTypes {
                id
                name
                slug
            }
        }
    }`;

  return await api.post<
    GraphqlTicketResponse,
    {
      query: string;
      variables: { forEvent: number; accessType: number };
    }
  >(token, '../graphql/public', undefined, {
    query: sendQuery,
    variables: {
      forEvent: eventId,
      accessType: accessTypeId,
    },
  });
};

// Derive each zone's sold_out flag and rows from its inventory fetch. A failed
// fetch (`null`) is distinct from a fetched-but-empty one: availability is
// unknown, so the incoming sold_out flag is kept rather than asserted, and the
// zone is omitted from inventoryByAccessType so the map treats its tables as
// unknown instead of dimming them all as unavailable.
export const deriveZoneInventories = <T extends { id: number; sold_out?: boolean }>(
  accessTypes: T[],
  results: Array<GraphqlTicketResponse | null>
): {
  accessTypes: Array<T & { sold_out: boolean }>;
  inventoryByAccessType: Record<number, TicketInventoryRow[]>;
} => {
  const inventoryByAccessType: Record<number, TicketInventoryRow[]> = {};
  const enriched = accessTypes.map((at, idx) => {
    const result = results[idx];
    if (result === null) {
      return { ...at, sold_out: at.sold_out ?? false };
    }
    // Filter once so a null row can't reach isTicketBookable (which would
    // throw and fail the whole event open via the caller's catch).
    const rows = (result.data?.ticketInventory || []).filter(Boolean);
    inventoryByAccessType[at.id] = rows;
    if (rows.length === 0) {
      return { ...at, sold_out: true };
    }
    // Match member app: inventory.every(inv => inv.sold_out)
    // But for tiered pricing items, the parent sold_out can lag behind
    // tier-level availability. Check tiers directly when present.
    const isSoldOut = rows.every((item) => !isTicketBookable(item));
    return { ...at, sold_out: isSoldOut };
  });
  return { accessTypes: enriched, inventoryByAccessType };
};

// Enrich access types with sold_out status derived from ticket inventory.
// The public GraphQL schema omits sold_out on AccessType, so we fetch
// ticketInventory for each type in parallel and derive it client-side.
export const enrichAccessTypesWithSoldOut = async (
  eventId: number,
  accessTypes: Array<{ id: number; sold_out?: boolean; [key: string]: any }>,
  token: string | null = null
): Promise<{
  accessTypes: Array<{ id: number; sold_out: boolean; [key: string]: any }>;
  // Each type's ticket rows from the same fetch, so callers (the venue map)
  // don't re-request inventory the sold-out derivation already loaded.
  inventoryByAccessType: Record<number, TicketInventoryRow[]>;
}> => {
  try {
    // A failed zone resolves to `null` (distinct from a fetched-but-empty
    // result) so it is never mistaken for sold out below.
    const inventoryPromises = accessTypes.map((at) =>
      graphqlTicketQuery(eventId, at.id, token).catch((error: unknown) => {
        Sentry.captureException(error, {
          extra: { eventId, accessTypeId: at.id },
        });
        return null;
      })
    );

    const inventoryResults = await Promise.all(inventoryPromises);

    return deriveZoneInventories(accessTypes, inventoryResults);
  } catch (error) {
    Sentry.captureException(error, { extra: { eventId } });
    return {
      accessTypes: accessTypes.map((at) => ({ ...at, sold_out: false })),
      inventoryByAccessType: {},
    };
  }
};

interface GraphqlAddOnResponse {
  data?: {
    addOnInventory?: Array<{
      id: number;
      price: number;
      price__currency: string;
      sold_out: boolean;
      guests: number;
      maximum_purchasable: number;
      item: {
        name?: string;
        description?: string;
      };
    }>;
  };
}

export const graphqlAddOnQuery = async (
  ticketItemId: number,
  token: string | null = null
): Promise<GraphqlAddOnResponse> => {
  const sendQuery = `query AddOnInventory($forItem: ID!) {
        addOnInventory(forItem: $forItem) {
            id
            price
            price__currency
            sold_out
            guests
            maximum_purchasable
            item {
                name
                description
            }
        }
    }`;

  return await api.post<
    GraphqlAddOnResponse,
    {
      query: string;
      variables: { forItem: number };
    }
  >(token, '../graphql/public', undefined, {
    query: sendQuery,
    variables: {
      forItem: ticketItemId,
    },
  });
};

/* ---------- Plaid / ACH ---------- */

export interface PlaidLinkTokenResponse {
  create_link_token: string;
  link_token?: string; // alias kept for compatibility
  expiration: string;
}

export interface PlaidCreatePaymentMethodResponse {
  payment_method: {
    id: string;
    bank_name: string;
    last4: string;
    stripe_id: string;
  };
}

export const createPlaidLinkToken = async (token: string): Promise<PlaidLinkTokenResponse> => {
  return api.post(token, API_URI.PLAID.CREATE_LINK_TOKEN());
};

export const createPlaidPaymentMethod = async (
  token: string,
  body: {
    public_token: string;
    account_id: string;
    context?: string;
    context_id?: number;
  }
): Promise<PlaidCreatePaymentMethodResponse> => {
  return api.post(token, API_URI.PLAID.CREATE_PAYMENT_METHOD(), undefined, body);
};

/* ---------- Custom Request (Widget) ---------- */

/**
 * Fetches available time slots and spend options for a custom request.
 * @param token - Auth token
 * @param restaurantId - Restaurant ID
 * @param date - Selected date (YYYY-MM-DD)
 * @param party - Party size
 * @returns Time slots and spend options
 */
export const getRequestOptions = async (
  token: string,
  restaurantId: number,
  date: string,
  party: number
): Promise<RequestOptionsResponse> => {
  return api.get(token, API_URI.RESTAURANT.REQUEST_OPTIONS(restaurantId), { date, party });
};

/**
 * Fetches custom request settings (price options, expiration, table types) via GraphQL.
 * @param restaurantId - Restaurant ID
 * @param token - Optional auth token
 * @param params - Optional date, party size, and time window to get restaurant-specific options
 * @returns Settings including price_options, table_options, expiration_options
 */
export const getCustomRequestSettings = async (
  restaurantId: number,
  token: string | null = null,
  params?: {
    date?: string;
    partySize?: number;
    startTime?: string;
    endTime?: string;
    seatingTypes?: string[];
  }
): Promise<CustomRequestSettingsResponse> => {
  const query = `query CustomRequestSettings(
    $id: ID!
    $date: String
    $party_size: Int
    $start_time: String
    $end_time: String
    $seating_types: [String]
  ) {
    customRequestSettings(
      id: $id
      date: $date
      party_size: $party_size
      start_time: $start_time
      end_time: $end_time
      seating_types: $seating_types
    ) {
      price_options {
        price
        acceptance_chance
        acceptance_label
        default
      }
      expiration_options {
        hours
        description
        default
      }
      table_options {
        id
        name
      }
      response_time {
        average_response_time_in_minutes
        average_response_time_as_text
        average_response_time_verbiage
      }
    }
  }`;

  return api.post<
    CustomRequestSettingsResponse,
    { query: string; variables: Record<string, unknown> }
  >(token, '../graphql/public', undefined, {
    query,
    variables: {
      id: restaurantId,
      date: params?.date,
      party_size: params?.partySize,
      start_time: params?.startTime,
      end_time: params?.endTime,
      seating_types: params?.seatingTypes,
    },
  });
};

/**
 * Previews a custom request to get pricing breakdown and cancellation policy.
 * @param token - Auth token
 * @param restaurantId - Restaurant ID
 * @param body - Preview request body
 * @returns Reservation preview with pricing
 */
export const previewCustomRequest = async (
  token: string,
  restaurantId: number,
  body: CustomRequestPreviewBody
): Promise<{ reservation: ConfirmationResponseReservation }> => {
  return api.post(token, API_URI.RESTAURANT.CUSTOM_REQUEST_PREVIEW(restaurantId), undefined, body);
};

/**
 * Submits a custom request reservation with payment pre-authorization.
 * @param token - Auth token
 * @param restaurantId - Restaurant ID
 * @param body - Custom request submission body
 * @returns Created reservation with status
 */
export const submitCustomRequest = async (
  token: string,
  restaurantId: number,
  body: CustomRequestSubmitBody
): Promise<{ status: string; reservation: { uuid: string } }> => {
  return api.post(token, API_URI.RESTAURANT.CUSTOM_REQUEST(restaurantId), undefined, body);
};
